import React from 'react';
import { Helmet } from 'react-helmet-async';
import './LegalPages.css';

const LAST_UPDATED = 'February 7, 2026';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <Helmet>
        <title>Privacy Policy | Tmper</title>
        <meta
          name="description"
          content="Privacy Policy for Tmper temporary file and image hosting. Learn what data we collect, how uploads are handled, and how cookie preferences work."
        />
        <link rel="canonical" href="https://tmper.app/privacy" />
        <meta property="og:title" content="Privacy Policy | Tmper" />
        <meta
          property="og:description"
          content="Privacy Policy for Tmper temporary file and image hosting."
        />
        <meta property="og:url" content="https://tmper.app/privacy" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="legal-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: {LAST_UPDATED}</p>

        <section>
          <h2>1. Information We Collect</h2>
          <h3>1.1 Files You Upload</h3>
          <p>
            When you upload files to Tmper (including images), we temporarily store your files to provide the hosting
            service and generate shareable links.
          </p>
          
          <h3>1.2 Automatically Collected Information</h3>
          <ul>
            <li>Browser ID (generated locally for anonymous usage tracking)</li>
            <li>IP address and basic browser information</li>
            <li>Upload timestamps and file metadata</li>
          </ul>

          <h3>1.3 Analytics and Cookies</h3>
          <p>We use Google Analytics to understand how visitors interact with our website. Google Analytics uses cookies to collect information such as:</p>
          <ul>
            <li>Pages visited and time spent on each page</li>
            <li>Device type, browser, and operating system</li>
            <li>Geographic location (country/city level)</li>
            <li>Referring websites and search terms</li>
            <li>User interactions and behavior patterns</li>
          </ul>
          <p>This data is processed by Google and helps us improve our service. For more information on how Google uses data, visit <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.</p>
          <p>You can opt out of Google Analytics by installing the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a>.</p>

          <h3>1.4 Optional Information</h3>
          <p>If you choose to sign in with Google, we may collect your Google account information (name, email) to provide a personalized experience.</p>
        </section>

        <section>
          <h2>2. How We Use Your Information</h2>
          <p>We use the collected information to:</p>
          <ul>
            <li>Provide and maintain the image hosting service</li>
            <li>Generate shareable links for your uploaded images</li>
            <li>Track upload history (stored locally in your browser)</li>
            <li>Analyze website usage and user behavior through Google Analytics</li>
            <li>Improve service performance, reliability, and user experience</li>
            <li>Prevent abuse and ensure service security</li>
          </ul>
        </section>

        <section>
          <h2>3. Data Storage and Retention</h2>
          <h3>3.1 Automatic Deletion</h3>
          <p><strong>All uploaded files are automatically deleted after 1 hour</strong> from our systems. This is a core feature of our temporary hosting service.</p>
          
          <h3>3.2 Local Storage</h3>
          <p>Upload history and browser preferences are stored locally in your browser using localStorage. This data never leaves your device.</p>

          <h3>3.3 Server Logs</h3>
          <p>Server access logs are retained for up to 30 days for security and operational purposes, then automatically deleted.</p>
        </section>

        <section>
          <h2>4. Data Sharing and Disclosure</h2>
          <p>We do not sell, trade, or share your personal information with third parties, except:</p>
          <ul>
            <li>To comply with legal obligations</li>
            <li>To protect our rights and prevent abuse</li>
            <li>With service providers necessary for service operation:
              <ul>
                <li>Cloudflare R2 (file storage)</li>
                <li>Google Analytics (website analytics)</li>
              </ul>
            </li>
          </ul>
          <p>These third-party service providers have their own privacy policies governing how they use, process, and protect your information.</p>
        </section>

        <section>
          <h2>5. Security Measures</h2>
          <p>We implement appropriate security measures including:</p>
          <ul>
            <li>HTTPS encryption for all data transmission</li>
            <li>Secure storage infrastructure (Cloudflare R2)</li>
            <li>Regular security monitoring and updates</li>
            <li>Automatic data deletion to minimize exposure</li>
          </ul>
        </section>

        <section>
          <h2>6. Cookies and Tracking Technologies</h2>
          <h3>6.1 What Are Cookies</h3>
          <p>Cookies are small text files stored on your device that help us analyze web traffic and improve user experience.</p>

          <h3>6.2 Types of Cookies We Use</h3>
          <ul>
            <li><strong>Essential Cookies:</strong> Required for the website to function properly (localStorage for upload history)</li>
            <li><strong>Analytics Cookies:</strong> Google Analytics cookies to understand website usage and performance</li>
          </ul>

          <h3>6.3 Managing Cookies</h3>
          <p>You can control and manage cookies in several ways:</p>
          <ul>
            <li>Most browsers allow you to refuse or accept cookies through settings</li>
            <li>You can delete cookies already stored on your device</li>
            <li>Install the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a></li>
          </ul>
          <p>Note: Disabling cookies may affect website functionality.</p>
        </section>

        <section>
          <h2>7. Your Rights and Choices</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access information about your uploads (via local history)</li>
            <li>Delete your local upload history at any time</li>
            <li>Opt out of Google Analytics tracking</li>
            <li>Manage or disable cookies in your browser settings</li>
            <li>Stop using the service at any time</li>
            <li>Request deletion of any personal data we may hold</li>
            <li>Contact us with privacy concerns</li>
          </ul>
        </section>

        <section>
          <h2>8. Children's Privacy</h2>
          <p>Our service is not directed to children under 13. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us.</p>
        </section>

        <section>
          <h2>9. International Data Transfers</h2>
          <p>Your data may be stored and processed in servers located outside your country of residence, including the United States (Google Analytics). We ensure appropriate safeguards are in place for such transfers.</p>
        </section>

        <section>
          <h2>10. Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.</p>
        </section>

        <section>
          <h2>11. Contact Information</h2>
          <p>If you have any questions about this Privacy Policy, please contact us at:</p>
          <p>Email: support@tmper.app</p>
          <p>Website: https://tmper.app</p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
